import { DialogButtons, ModalRef, ModalResult, useTranslation } from '@msdk/common'
import {
  ActionMenuBehaviour,
  Entity,
  EntityContext,
  FormMode,
  FormNode,
  GenericFieldValue,
  ServerOperationResult,
} from '@msdk/definitions'
import {
  ButtonIcon,
  CloseIcon,
  COLORS,
  createComponentWrapperWithRef,
  css,
  FormFooterWrapper,
  FormHeaderWrapper,
  FormWrapper,
  styled,
  uiTokens,
} from '@msdk/ui'
import React, { useEffect, useRef, useState } from 'react'

import { useDialogModal } from '../../hooks'
import { useModalForm } from '../../hooks/form'
import { useEntityFromCollection, useEntryFromCollectionWithInternalId } from '../../state'
import { stringFormat, useTabs } from '../../utils'
import { saveValidated, ValidatedForm } from '../../utils/save-validation'
import { DynamicForm } from '../DynamicForm'
import { CustomLogicInvoker } from '../DynamicForm/custom-logic-invoker'
import { ActionsContainer } from '../FormActions/ActionsContainer'
import { FormHeader } from '../FormHeader/FormHeader'
import { FormSubHeader } from '../FormHeader/FormSubHeader'

const MuiStyledFormWrapper = styled(FormWrapper)`
  border-top: 1px solid ${COLORS.divider};
  border-bottom: 1px solid ${COLORS.divider};
  overflow: auto;
  flex: 1;
`

const NovaStyledFormWrapper = styled(FormWrapper)`
  border-top: 1px solid ${uiTokens.uiStroke.neutral};
  border-bottom: 1px solid ${uiTokens.uiStroke.neutral};
  overflow: auto;
  flex: 1;
`

const MuiStyledDynamicForm = styled(DynamicForm)`
  padding: 24px 24px 16px;
`

const NovaStyledDynamicForm = styled(DynamicForm)`
  padding: ${uiTokens.spacing.padding.medium}px;
`

const StyledDynamicForm = createComponentWrapperWithRef(
  {
    mui: MuiStyledDynamicForm,
    nova: NovaStyledDynamicForm,
  },
  'StyledDynamicForm',
)

//I need to override the default FormHeaderWrapper for the Nova theme only in modal
const NovaFormHeaderWrapper = styled(FormHeaderWrapper)`
  height: auto;
  min-height: ${uiTokens.sizing.uiControl.height.large}px;
`

const StyledFormHeaderWrapper = createComponentWrapperWithRef(
  {
    mui: FormHeaderWrapper,
    nova: NovaFormHeaderWrapper,
  },
  'StyledFormHeaderWrapper',
)

const LibFormWrapper = createComponentWrapperWithRef(
  {
    mui: MuiStyledFormWrapper,
    nova: NovaStyledFormWrapper,
    novaProps: {
      sx: {
        scrollbarGutter: 'stable',
      },
    },
  },
  'LibFormWrapper',
)

const StyledFormWrapper = styled(LibFormWrapper, { shouldForwardProp: (props) => props !== '$isFullScreen' })`
  ${({ $isFullScreen }) =>
    $isFullScreen &&
    css`
      background-color: ${COLORS.components.form.background};
      > .MuiBox-root {
        max-width: 1024px;
      }
    `}
`

const HidingDiv = styled('div')`
  display: none;
  &.dynamic-form-visible {
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;
    overflow: hidden;
  }
  .MuiDialogContent-root:has(> &) {
    padding: 0;
    overflow-y: hidden;
    display: flex;
  }
`
const StyledButtonIcon = styled(ButtonIcon)`
  align-self: flex-start;
  flex-shrink: 0;
`

export interface ModalFormDynamicCloseArgs extends ModalResult {
  newFormRequired: string
  mode: FormMode
}

interface Props {
  entityName: string
  formName: string
  id: string
  mode: FormMode
  modalRef?: ModalRef
  prefilledFields?: Record<string, GenericFieldValue>
  containerEntity?: Entity
}

export function ModalFormDynamic(props: Props) {
  const validationRef = useRef<(() => Promise<ValidatedForm | null>) | null>(null)
  const dialog = useDialogModal()
  const t = useTranslation()

  const {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    onSave,
    acl,
    invoker,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
    onClose: onCloseUseForm,
  } = useModalForm(props.entityName, props.formName, props.id, props.prefilledFields, props.containerEntity)

  const { selectedTab, isFirstTab, isLastTab, updateSelectedTab, startingTab, moveToTab } = useTabs({
    form: form as FormNode,
  })

  useEffect(() => {
    const openErrorModal = async () => {
      const message = t('LBL_ENTITY_NOT_FOUND_MESSAGE', 'The specified {0} was not found')
      const title = t('LBL_ENTITY_NOT_FOUND_TITLE', '{0} not found')
      const formattedMessage = stringFormat(message, t(entityModel.idml, entityModel.description))
      const formattedTitle = stringFormat(title, t(entityModel.idml, entityModel.description))
      await dialog.createModal(formattedTitle, formattedMessage, DialogButtons.Ok)
      props.modalRef?.close()
    }

    if (serverError) {
      openErrorModal()
    }
  }, [serverError])

  const [isDynamicFormReady, setIsDynamicFormReady] = useState(false)

  useEntryFromCollectionWithInternalId(entity?.internalId as string)

  const [mode, setMode] = useState<FormMode>(props.mode)

  const isFullScreen = !!(props.modalRef?.component as React.ReactElement)?.props?.isFullScreen

  const handleModeChange = (newMode: FormMode, formName: string) => {
    if (formName !== props.formName) {
      const args: ModalFormDynamicCloseArgs = {
        newFormRequired: formName,
        mode: newMode,
      }
      props.modalRef?.close(args)
      return
    }
    setMode(newMode)
  }

  /**
   * the entityContext coming from the entityCollection
   * this should be always up to date with the changes in the form
   */
  const entityContext = useEntityFromCollection(entity?.id as string)
  /**
   * this base entity context is used in the beginning as the entity collection
   * might not be yet populated at the first render of the form
   */
  const baseEntityContext = new EntityContext(entity as Entity, form as FormNode, additionalEntities, formModel)

  const onSaveClick = async (actionId?: string) => {
    const res = await saveValidated(validationRef.current, onSave, actionId)
    if (res.result === ServerOperationResult.Success) {
      props.modalRef?.close(res.entities)
    }
    return res
  }

  const onClose = async () => {
    const { canLeaveForm } = await onCloseUseForm(dialog)
    if (canLeaveForm) {
      props.modalRef?.close()
    }
  }

  const classNames = ['ModalFormDynamic']
  classNames.push(isDynamicFormReady ? 'dynamic-form-visible' : 'dynamic-form-hidden')
  return (
    <HidingDiv className={classNames.join(' ')}>
      {formModel && (
        <>
          <StyledFormHeaderWrapper className="modal-form-header" $isModal={true} $isFullScreen={isFullScreen}>
            {!formModel.hideFormHeader && (
              <FormHeader
                acl={acl}
                entity={entityContext ?? baseEntityContext}
                entityModel={entityModel}
                formModel={formModel}
                invoker={invoker as CustomLogicInvoker}
                onDelete={onDelete}
                onMoveToBin={onMoveToBin}
                onReloadEntity={onReloadEntity}
                onRestore={onRestore}
                onSave={onSaveClick}
                rootNode={form as FormNode}
                mode={mode}
                entityLoaded={entityLoaded}
                rebindEntitiy={rebindEntity}
                modalRef={props.modalRef}
                onModeChange={handleModeChange}
                isFullScreen={isFullScreen}
              />
            )}
            <StyledButtonIcon onClick={onClose}>
              <CloseIcon />
            </StyledButtonIcon>
          </StyledFormHeaderWrapper>
          <StyledFormWrapper $isFullScreen={isFullScreen}>
            <FormSubHeader
              entity={entityContext ?? baseEntityContext}
              entityModel={entityModel}
              formModel={formModel}
              rootNode={form as FormNode}
              mode={mode}
              isFullScreen={isFullScreen}
              onModeChange={handleModeChange}
              onReloadEntity={onReloadEntity}
            />
            <StyledDynamicForm
              root={form as FormNode}
              entityLoaded={entityLoaded}
              formModel={formModel}
              entity={entity}
              additionalEntities={additionalEntities}
              entityModel={entityModel}
              acl={acl}
              invoker={invoker as CustomLogicInvoker}
              mode={mode}
              modalRef={props.modalRef}
              onRenderCompleted={() => setIsDynamicFormReady(true)}
              validationRef={validationRef}
              selectedTab={selectedTab}
              onChangeTab={updateSelectedTab}
              startingTab={startingTab}
              moveToTab={moveToTab}
            />
          </StyledFormWrapper>
          {mode === 'Edit' && formModel.actionMenuBehaviour === ActionMenuBehaviour.Default && (
            <FormFooterWrapper>
              <ActionsContainer
                acl={acl}
                entity={entityContext ?? baseEntityContext}
                entityModel={entityModel}
                formModel={formModel}
                invoker={invoker as CustomLogicInvoker}
                onDelete={onDelete}
                onMoveToBin={onMoveToBin}
                onReloadEntity={onReloadEntity}
                onRestore={onRestore}
                onSave={onSaveClick}
                onClose={onClose}
                rootNode={form as FormNode}
                entityLoaded={entityLoaded}
                isModalActions={true}
                rebindEntity={rebindEntity}
                isFullScreen={isFullScreen}
                selectedTab={selectedTab}
                isFirstTab={isFirstTab()}
                isLastTab={isLastTab()}
                moveToTab={moveToTab}
              />
            </FormFooterWrapper>
          )}
        </>
      )}
    </HidingDiv>
  )
}
